import { useState, useEffect, useCallback } from 'react';
import { userPreferencesService } from '../services/userPreferencesService';
import { UserPreferences, BCPAssessmentData, PackageRecommendation } from '../types/userPreferences';

export const useUserPreferences = () => {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPreferences = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const userPreferences = await userPreferencesService.getUserPreferences();
      setPreferences(userPreferences);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load preferences');
      console.error('Error loading user preferences:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const submitAssessment = useCallback(async (assessment: BCPAssessmentData): Promise<PackageRecommendation> => {
    try {
      setError(null);
      const recommendation = await userPreferencesService.submitAssessment(assessment);
      // Reload preferences to get the updated data
      await loadPreferences();
      return recommendation;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit assessment';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [loadPreferences]);

  const updatePreferences = useCallback(async (updates: Partial<UserPreferences>) => {
    try {
      setError(null);
      const updatedPreferences = await userPreferencesService.updateUserPreferences(updates);
      setPreferences(updatedPreferences);
      return updatedPreferences;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update preferences';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const deletePreferences = useCallback(async () => {
    try {
      setError(null);
      await userPreferencesService.deleteUserPreferences();
      setPreferences(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete preferences';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const hasPreferences = preferences !== null;
  const isFirstBCP = !hasPreferences;

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  return {
    preferences,
    loading,
    error,
    hasPreferences,
    isFirstBCP,
    loadPreferences,
    submitAssessment,
    updatePreferences,
    deletePreferences
  };
};
